import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { analyzeProperty } from '../services/authService';

type UploadMode = 'files' | 'url';

interface FileEntry {
  file: File;
  preview: string | null;
}

interface UploadState {
  files: FileEntry[];
  url: string;
  mode: UploadMode;
}

export default function RatePage() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [upload, setUpload] = useState<UploadState>({
    files: [],
    url: '',
    mode: 'files',
  });
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorType, setErrorType] = useState<'error' | 'url_blocked'>('error');
  const [result, setResult] = useState<{
    extracted: any;
    valuation: string;
    shortConclusion?: string;
    comparables: any[];
    priceEstimate: any;
  } | null>(null);
  const [showFullReport, setShowFullReport] = useState(false);
  const [bgImage, setBgImage] = useState<{ filename: string; opacity: number; blend_mode: string; blur: number } | null>(null);
  const [urlEnabled, setUrlEnabled] = useState(false);

  // Fetch Rate page background image settings
  useEffect(() => {
    fetch('/api/admin/rate-page-image-settings')
      .then(r => r.json())
      .then(data => {
        if (data.success && data.data?.enabled && data.data?.filename) {
          setBgImage(data.data);
        }
      })
      .catch(() => {});
  }, []);

  // Fetch Rate feature toggles (URL field visibility)
  useEffect(() => {
    fetch('/api/admin/rate-features')
      .then(r => r.json())
      .then(data => {
        if (data.success && data.data) {
          setUrlEnabled(!!data.data.url_enabled);
        }
      })
      .catch(() => {});
  }, []);

  const addFiles = useCallback((newFiles: FileList | File[]) => {
    const entries: FileEntry[] = [];
    for (const file of Array.from(newFiles)) {
      const isPDF = file.type === 'application/pdf';
      const isImage = file.type.startsWith('image/');
      if (!isPDF && !isImage) continue;
      entries.push({ file, preview: isImage ? URL.createObjectURL(file) : null });
    }
    if (entries.length === 0) {
      setError('Bitte laden Sie PDF-Dateien oder Bilder (Screenshots) hoch.');
      return;
    }
    setError(null);
    setUpload(prev => ({
      files: [...prev.files, ...entries].slice(0, 10), // max 10 files
      url: '',
      mode: 'files',
    }));
  }, []);

  const removeFile = useCallback((index: number) => {
    setUpload(prev => {
      const updated = [...prev.files];
      const removed = updated.splice(index, 1);
      removed.forEach(e => { if (e.preview) URL.revokeObjectURL(e.preview); });
      return { ...prev, files: updated };
    });
  }, []);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) addFiles(e.target.files);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [addFiles]);

  const handleUrlChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setUpload({ files: [], url: e.target.value, mode: 'url' });
    setError(null);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files);
    }
  }, [addFiles]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleAnalyze = useCallback(async () => {
    if (!isAuthenticated) {
      navigate('/#lab-auth');
      return;
    }

    const hasFiles = upload.files.length > 0;
    const hasUrl = upload.url.trim().length > 0;

    if (!hasFiles && !hasUrl) {
      setError(urlEnabled
        ? 'Bitte laden Sie ein Dokument hoch oder geben Sie eine URL ein.'
        : 'Bitte laden Sie ein Dokument hoch (PDF oder Screenshot).');
      return;
    }

    setIsAnalyzing(true);
    setError(null);
    setErrorType('error');
    setResult(null);

    try {
      const response = await analyzeProperty({
        files: hasFiles ? upload.files.map(f => f.file) : undefined,
        url: upload.url.trim() || undefined,
      });
      setResult({
        extracted: response.extracted,
        valuation: response.valuation,
        shortConclusion: response.shortConclusion || response.priceEstimate?.shortConclusion || '',
        comparables: response.comparables,
        priceEstimate: response.priceEstimate,
      });
      setShowFullReport(false);
    } catch (err: any) {
      if (err.errorType === 'url_blocked') {
        setErrorType('url_blocked');
        setError('Diese Webseite blockiert den Zugriff. Bitte fügen Sie Screenshots oder das PDF dieses Inserats oder der Anzeige ein, um die Analyse durchführen zu können.');
      } else if (err.errorType === 'pdf_no_text') {
        setErrorType('url_blocked'); // reuse same amber/info style
        setError('Aus dem PDF konnte kein Text extrahiert werden. Bitte laden Sie stattdessen Screenshots der einzelnen Seiten hoch.');
      } else {
        setErrorType('error');
        setError(err.message || 'Analyse fehlgeschlagen');
      }
    } finally {
      setIsAnalyzing(false);
    }
  }, [upload, isAuthenticated, navigate]);

  const clearUpload = useCallback(() => {
    upload.files.forEach(e => { if (e.preview) URL.revokeObjectURL(e.preview); });
    setUpload({ files: [], url: '', mode: 'files' });
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [upload.files]);

  return (
    <div className="rate-page">
      {bgImage && (
        <div className="rate-page-bg" style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 0, pointerEvents: 'none',
          backgroundImage: `url(/uploads/${bgImage.filename})`,
          backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed',
          opacity: bgImage.opacity / 100,
          mixBlendMode: (bgImage.blend_mode || 'normal') as any,
          filter: bgImage.blur > 0 ? `blur(${bgImage.blur}px)` : undefined,
        }} />
      )}
      <div className="rate-hero">
        <div className="rate-hero-content">
          <h1 className="rate-title">
            SurrealEstate<span className="rate-title-accent">Rate</span>
          </h1>
          <p className="rate-subtitle">
            Laden Sie ein Immobilien-Inserat hoch und erhalten Sie eine KI-gestützte Marktbewertung
            mit detaillierter Analyse und Empfehlung.
          </p>
        </div>
      </div>

      <div className="rate-upload-section">
        {/* Upload area */}
        <div
          className={`rate-dropzone ${upload.files.length > 0 ? 'rate-dropzone--has-file' : ''}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,image/*"
            multiple
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          {upload.files.length > 0 ? (
            <div className="rate-files-list" onClick={e => e.stopPropagation()}>
              <div className="rate-files-grid">
                {upload.files.map((entry, idx) => (
                  <div key={idx} className="rate-file-thumb">
                    {entry.preview ? (
                      <img src={entry.preview} alt={`Preview ${idx + 1}`} className="rate-thumb-img" />
                    ) : (
                      <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" className="rate-file-icon">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                      </svg>
                    )}
                    <span className="rate-thumb-name">{entry.file.name}</span>
                    <button className="rate-thumb-remove" onClick={() => removeFile(idx)} title="Entfernen">
                      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                    </button>
                  </div>
                ))}
                {/* Add more button */}
                {upload.files.length < 10 && (
                  <div className="rate-file-thumb rate-file-thumb--add" onClick={() => fileInputRef.current?.click()}>
                    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="var(--primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                    <span>Weitere</span>
                  </div>
                )}
              </div>
              <div className="rate-files-actions">
                <span className="rate-files-count">{upload.files.length} Datei{upload.files.length !== 1 ? 'en' : ''} ausgewählt (max. 10)</span>
                <button className="rate-clear-btn" onClick={clearUpload}>
                  Alle entfernen
                </button>
              </div>
            </div>
          ) : (
            <div className="rate-dropzone-content">
              <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="var(--primary)" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" className="rate-upload-icon">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              <p className="rate-dropzone-text">PDFs oder Screenshots des Inserats oder der Immobilienanzeige hineinziehen</p>
              <p className="rate-dropzone-hint">oder klicken zum Auswählen · Mehrere Dateien möglich</p>
            </div>
          )}
        </div>

        {/* URL input – only shown when enabled via admin */}
        {urlEnabled && (
          <div className="rate-url-section">
            <div className="rate-divider">
              <span className="rate-divider-text">oder</span>
            </div>
            <div className="rate-url-input-wrapper">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="var(--text-muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="rate-url-icon">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
              </svg>
              <input
                type="url"
                className="rate-url-input"
                placeholder="URL des Inserats einfügen (z.B. immoscout24.ch, homegate.ch)"
                value={upload.url}
                onChange={handleUrlChange}
              />
            </div>
          </div>
        )}

        {/* Error / URL blocked hint */}
        {error && (
          <div className={errorType === 'url_blocked' ? 'rate-url-blocked-hint' : 'rate-error'}>
            {errorType === 'url_blocked' && (
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            )}
            {error}
          </div>
        )}

        {/* Analyze button */}
        <button
          className="rate-analyze-btn"
          onClick={handleAnalyze}
          disabled={isAnalyzing || (upload.files.length === 0 && !upload.url.trim())}
        >
          {isAnalyzing ? (
            <>
              <div className="spinner spinner--small" /> Analyse läuft...
            </>
          ) : (
            <>
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
              </svg>
              Immobilie bewerten / analysieren
            </>
          )}
        </button>

        {/* Valuation result */}
        {result && (
          <div className="rate-result">
            {/* Visual Gauge + Short Conclusion */}
            <div className="rate-verdict-card">
              <ValuationGauge
                assessment={result.priceEstimate?.assessment || 'unknown'}
                assessmentLabel={result.priceEstimate?.assessmentLabel || 'Nicht bestimmbar'}
                deviationPercent={result.priceEstimate?.deviationPercent ?? null}
                subjectPrice={result.extracted?.price_chf || null}
                avgPrice={result.priceEstimate?.avgPrice || null}
              />
              {/* Data confidence indicator */}
              {result.priceEstimate?.message && (
                <p className="rate-verdict-confidence" style={{
                  textAlign: 'center',
                  fontSize: '12px',
                  color: result.priceEstimate.confidence === 'web' ? '#f59e0b' : 
                         result.priceEstimate.confidence === 'low' ? '#f59e0b' : '#94a3b8',
                  marginTop: '-4px',
                  marginBottom: '8px',
                  fontStyle: 'italic',
                }}>
                  {result.priceEstimate.confidence === 'web' ? '⚠ ' : ''}{result.priceEstimate.message}
                </p>
              )}
              {(result.shortConclusion || result.priceEstimate?.shortConclusion) && (
                <p className="rate-verdict-conclusion">
                  {result.shortConclusion || result.priceEstimate?.shortConclusion}
                </p>
              )}
              {result.extracted?.price_chf && (
                <div className="rate-verdict-prices">
                  <span className="rate-verdict-price-item">
                    <span className="rate-verdict-price-label">Objektpreis</span>
                    <span className="rate-verdict-price-value">CHF {Number(result.extracted.price_chf).toLocaleString('de-CH')}</span>
                    {result.priceEstimate?.subjectPricePerM2 && <span className="rate-verdict-price-sub">{result.priceEstimate.subjectPricePerM2.toLocaleString('de-CH')} CHF/m²</span>}
                  </span>
                  {result.priceEstimate?.avgPrice && (
                    <span className="rate-verdict-price-item">
                      <span className="rate-verdict-price-label">Marktdurchschnitt</span>
                      <span className="rate-verdict-price-value">CHF {result.priceEstimate.avgPrice.toLocaleString('de-CH')}</span>
                      {result.priceEstimate.avgPricePerM2 && <span className="rate-verdict-price-sub">{result.priceEstimate.avgPricePerM2.toLocaleString('de-CH')} CHF/m²</span>}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Extracted data summary */}
            <div className="rate-extracted">
              <h3>Erkannte Objektdaten</h3>
              <div className="rate-extracted-grid">
                {result.extracted?.title && <div className="rate-extracted-item"><span className="rate-extracted-label">Titel</span><span className="rate-extracted-value">{result.extracted.title}</span></div>}
                {result.extracted?.city && <div className="rate-extracted-item"><span className="rate-extracted-label">Standort</span><span className="rate-extracted-value">{result.extracted.city}{result.extracted.canton ? `, ${result.extracted.canton}` : ''}</span></div>}
                {result.extracted?.rooms && <div className="rate-extracted-item"><span className="rate-extracted-label">Zimmer</span><span className="rate-extracted-value">{result.extracted.rooms}</span></div>}
                {result.extracted?.living_space_m2 && <div className="rate-extracted-item"><span className="rate-extracted-label">Wohnfläche</span><span className="rate-extracted-value">{result.extracted.living_space_m2} m²</span></div>}
                {result.extracted?.year_built && <div className="rate-extracted-item"><span className="rate-extracted-label">Baujahr</span><span className="rate-extracted-value">{result.extracted.year_built}</span></div>}
                {result.extracted?.property_type && <div className="rate-extracted-item"><span className="rate-extracted-label">Typ</span><span className="rate-extracted-value">{result.extracted.property_type}</span></div>}
              </div>
            </div>

            {/* Toggle for full report */}
            <button className="rate-report-toggle" onClick={() => setShowFullReport(!showFullReport)}>
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
              {showFullReport ? 'Detailbericht ausblenden' : 'Detailbericht anzeigen'}
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: showFullReport ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>

            {/* AI valuation text (full report) */}
            {showFullReport && (
              <>
                <div className="rate-valuation-text" dangerouslySetInnerHTML={{ __html: renderMarkdown(result.valuation) }} />

                {/* Comparables */}
                {result.comparables.length > 0 && (
                  <div className="rate-comparables">
                    <h3>Vergleichsobjekte ({result.comparables.length})</h3>
                    <div className="rate-comparables-list">
                      {result.comparables.map((c: any, i: number) => (
                        <div key={i} className="rate-comparable-card">
                          <span className="rate-comparable-title">{c.title || `Objekt ${c.id}`}</span>
                          <span className="rate-comparable-detail">
                            {c.city}{c.canton ? `, ${c.canton}` : ''} · {c.rooms || '?'} Zi. · {c.living_space_m2 || '?'} m²
                          </span>
                          <span className="rate-comparable-price">CHF {c.price_chf ? Number(c.price_chf).toLocaleString('de-CH') : '?'}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            <div className="rate-result-actions">
              <button
                className="rate-download-btn"
                onClick={async () => {
                  const title = result.extracted?.title || 'Immobilienbewertung';
                  const city = result.extracted?.city || '';
                  const canton = result.extracted?.canton || '';
                  const location = [city, canton].filter(Boolean).join(', ');
                  const pdfContainer = document.createElement('div');
                  pdfContainer.style.cssText = 'padding:24px;font-family:system-ui,-apple-system,sans-serif;color:#1e293b;font-size:13px;line-height:1.6;max-width:700px;';
                  pdfContainer.innerHTML = `
                    <style>
                      * { color: #1e293b !important; }
                      h1 { color: #00CED1 !important; }
                      h2 { font-size: 15px; margin: 18px 0 6px; border-bottom: 1px solid #e2e8f0; padding-bottom: 4px; }
                      h3 { font-size: 14px; margin: 14px 0 4px; color: #0891b2 !important; }
                      h4 { font-size: 13px; margin: 10px 0 3px; color: #0e7490 !important; }
                      h5 { font-size: 13px; margin: 8px 0 2px; color: #0891b2 !important; }
                      strong { color: #0f172a !important; }
                      table { width: 100%; border-collapse: collapse; margin: 8px 0 12px; font-size: 12px; }
                      td, th { padding: 6px 10px; border: 1px solid #cbd5e1; color: #1e293b !important; }
                      tr:first-child td { background: #f1f5f9; font-weight: 600; }
                      ul { padding-left: 18px; margin: 4px 0 8px; }
                      li { margin-bottom: 2px; }
                      hr { border: none; border-top: 1px solid #e2e8f0; margin: 12px 0; }
                      p { margin: 0 0 6px; }
                    </style>
                    <h1 style="font-size:18px;margin:0 0 4px;">SurrealEstateRate – Analyse</h1>
                    <p style="color:#64748b !important;font-size:11px;margin:0 0 16px;">${new Date().toLocaleDateString('de-CH')}</p>
                    <div style="margin-bottom:16px;padding:10px 14px;background:#f1f5f9;border-radius:8px;">
                      <div style="font-weight:600;margin-bottom:6px;">${title}</div>
                      ${location ? `<div style="font-size:12px;color:#64748b !important;">${location}</div>` : ''}
                      ${result.extracted?.rooms ? `<div style="font-size:12px;color:#64748b !important;">${result.extracted.rooms} Zimmer · ${result.extracted.living_space_m2 || '?'} m² · CHF ${result.extracted.price_chf ? Number(result.extracted.price_chf).toLocaleString('de-CH') : '?'}</div>` : ''}
                      ${result.priceEstimate?.assessmentLabel ? `<div style="font-size:12px;margin-top:6px;"><strong style="color:#0891b2 !important;">Bewertung:</strong> ${result.priceEstimate.assessmentLabel}${result.priceEstimate.deviationPercent != null ? ` (${result.priceEstimate.deviationPercent > 0 ? '+' : ''}${result.priceEstimate.deviationPercent}%)` : ''}</div>` : ''}
                    </div>
                    ${(result.shortConclusion || result.priceEstimate?.shortConclusion) ? `<div style="margin-bottom:16px;padding:10px 14px;background:#f0fdfa;border-left:3px solid #00CED1;border-radius:4px;font-style:italic;">${result.shortConclusion || result.priceEstimate?.shortConclusion}</div>` : ''}
                    <div>${renderMarkdown(result.valuation)}</div>
                  `;
                  const html2pdf = (await import('html2pdf.js')).default;
                  html2pdf().set({
                    margin: [10, 10, 10, 10],
                    filename: `Analyse_${(title || 'Immobilie').replace(/[^a-zA-Z0-9äöüÄÖÜ]/g, '_').substring(0, 40)}_${new Date().toISOString().slice(0, 10)}.pdf`,
                    image: { type: 'jpeg', quality: 0.98 },
                    html2canvas: { scale: 2, useCORS: true },
                    jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
                  }).from(pdfContainer).save();
                }}
              >
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Als PDF herunterladen
              </button>
              <button className="rate-new-btn" onClick={() => { setResult(null); clearUpload(); setShowFullReport(false); }}>
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg> Neue Bewertung starten
              </button>
            </div>
          </div>
        )}

        {/* Feature cards (only show when no result) */}
        {!result && <div className="rate-features">
          <div className="rate-feature-card">
            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="var(--primary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
            <h3>PDF & Screenshots</h3>
            <p>Laden Sie Inserate als PDF oder Screenshot hoch. Die KI extrahiert alle relevanten Daten automatisch.</p>
          </div>
          <div className="rate-feature-card">
            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="var(--primary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
            </svg>
            <h3>Marktanalyse</h3>
            <p>Vergleich mit unserer Datenbank und öffentlichen Quellen. Ist der Preis fair, zu hoch oder ein Schnäppchen?</p>
          </div>
          <div className="rate-feature-card">
            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="var(--primary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
            </svg>
            <h3>Detailbericht</h3>
            <p>Ausführliche Bewertung mit Preisschätzung pro m², Vergleichsobjekten und Kauf-/Pass-Empfehlung.</p>
          </div>
        </div>}
      </div>
    </div>
  );
}

/** Visual semicircular gauge for valuation verdict */
function ValuationGauge({ assessment, assessmentLabel, deviationPercent, subjectPrice, avgPrice }: {
  assessment: string;
  assessmentLabel: string;
  deviationPercent: number | null;
  subjectPrice: number | null;
  avgPrice: number | null;
}) {
  // Compute needle angle: -30% → 170°, 0% → 90°, +30% → 10° (in degrees from right)
  // Clamped to [8, 172]
  const deviation = deviationPercent ?? 0;
  const clampedDev = Math.max(-35, Math.min(35, deviation));
  const needleAngleDeg = 90 - (clampedDev / 35) * 82;
  const needleAngleRad = (needleAngleDeg * Math.PI) / 180;

  // Gauge geometry
  const cx = 140, cy = 125, outerR = 95, needleR = 72;
  const needleX = cx + needleR * Math.cos(needleAngleRad);
  const needleY = cy - needleR * Math.sin(needleAngleRad);

  // Dot at needle tip
  const dotR = 5;

  // Color for the central text
  const verdictColor = assessment === 'below' ? '#22c55e'
    : assessment === 'above' ? '#f97316'
    : assessment === 'fair' ? '#00CED1'
    : '#94a3b8';

  // Arc path helper (for thick stroke arcs)
  function arcPath(startDeg: number, endDeg: number, r: number): string {
    const startRad = (startDeg * Math.PI) / 180;
    const endRad = (endDeg * Math.PI) / 180;
    const x1 = cx + r * Math.cos(startRad);
    const y1 = cy - r * Math.sin(startRad);
    const x2 = cx + r * Math.cos(endRad);
    const y2 = cy - r * Math.sin(endRad);
    const largeArc = Math.abs(endDeg - startDeg) > 180 ? 1 : 0;
    // sweep: clockwise when going from higher angle to lower
    return `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 0 ${x2} ${y2}`;
  }

  return (
    <div className="rate-gauge">
      <svg viewBox="0 0 280 155" className="rate-gauge-svg">
        {/* Background arc segments */}
        <path d={arcPath(175, 120, outerR)} fill="none" stroke="#22c55e" strokeWidth="14" strokeLinecap="round" opacity="0.3" />
        <path d={arcPath(120, 60, outerR)} fill="none" stroke="#00CED1" strokeWidth="14" strokeLinecap="round" opacity="0.3" />
        <path d={arcPath(60, 5, outerR)} fill="none" stroke="#f97316" strokeWidth="14" strokeLinecap="round" opacity="0.3" />

        {/* Gauge labels */}
        <text x="18" y="130" fill="#22c55e" fontSize="9" fontWeight="500" textAnchor="start" opacity="0.8">Unter</text>
        <text x="140" y="25" fill="#00CED1" fontSize="9" fontWeight="500" textAnchor="middle" opacity="0.8">Fair</text>
        <text x="262" y="130" fill="#f97316" fontSize="9" fontWeight="500" textAnchor="end" opacity="0.8">Über</text>

        {/* Needle line */}
        <line x1={cx} y1={cy} x2={needleX} y2={needleY} stroke={verdictColor} strokeWidth="2.5" strokeLinecap="round" />
        {/* Needle dot */}
        <circle cx={needleX} cy={needleY} r={dotR} fill={verdictColor} />
        {/* Center dot */}
        <circle cx={cx} cy={cy} r="6" fill="rgba(15, 23, 42, 0.9)" stroke={verdictColor} strokeWidth="2" />

        {/* Verdict text */}
        <text x={cx} y={cy + 28} fill={verdictColor} fontSize="16" fontWeight="700" textAnchor="middle">{assessmentLabel}</text>
        {deviationPercent !== null && (
          <text x={cx} y={cy + 44} fill="var(--text-secondary, #94a3b8)" fontSize="11" textAnchor="middle">
            {deviationPercent > 0 ? '+' : ''}{deviationPercent}% vs. Markt
          </text>
        )}
      </svg>
    </div>
  );
}

/** Simple markdown to HTML for valuation text */
function renderMarkdown(md: string): string {
  let html = md.replace(/\r\n/g, '\n');
  // Tables
  html = html.replace(/(?:^\|.+\|$\n?)+/gm, (block) => {
    const rows = block.trim().split('\n').filter(r => r.trim());
    const htmlRows = rows
      .filter(row => !/^[\s|\-:]+$/.test(row))
      .map(row => {
        const cells = row.split('|').filter(c => c !== '').map(c => c.trim());
        return '<tr>' + cells.map(c => `<td>${c}</td>`).join('') + '</tr>';
      })
      .join('');
    return `<table class="rate-table">${htmlRows}</table>\n`;
  });
  html = html
    .replace(/^#### (.+)$/gm, '<h5>$1</h5>')
    .replace(/^### (.+)$/gm, '<h4>$1</h4>')
    .replace(/^## (.+)$/gm, '<h3>$1</h3>')
    .replace(/^# (.+)$/gm, '<h2>$1</h2>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/(?<!\w)\*([^*]+?)\*(?!\w)/g, '<em>$1</em>')
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    .replace(/((?:<li>.*<\/li>\n?)+)/g, '<ul>$1</ul>')
    .replace(/^---+$/gm, '<hr />')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/\n\n/g, '</p><p>')
    .replace(/\n/g, '<br />')
    .replace(/^/, '<p>')
    .replace(/$/, '</p>');
  html = html
    .replace(/<p>\s*<\/p>/g, '')
    .replace(/<p>(<h[2345]>)/g, '$1')
    .replace(/(<\/h[2345]>)<\/p>/g, '$1')
    .replace(/<p>(<ul>)/g, '$1')
    .replace(/(<\/ul>)<\/p>/g, '$1')
    .replace(/<p>(<table)/g, '$1')
    .replace(/(<\/table>)<\/p>/g, '$1')
    .replace(/<p>(<hr \/>)/g, '$1')
    .replace(/(<hr \/>)<\/p>/g, '$1')
    .replace(/(<\/h[2345]>)<br \/>/g, '$1')
    .replace(/<br \/>(<h[2345]>)/g, '$1');
  return html;
}
